// Static descriptions and per-level effect formatters for each building type.
// Lives on the client because it's pure UI copy. The numeric constants here
// must stay in sync with the C# side (WarService, CastleService) until we
// expose them via API.

const HospitalCapacityPerLevel = 7000;
const TempleQueueCapacityPerLevel = 5000;
// Time reduction per Builder Hut level. MUST mirror
// CastleService.BuilderHutTimeBonusPerLevel on the server, otherwise the meta
// row and the server-computed effect string disagree.
const BuilderHutPctPerLevel = 0.02;
const AcademyPctPerLevel = 0.0;
// Defensive structures (Wall / ArrowTower / TrapField) — must mirror
// CastleService.DefensiveStructureBonusPerLevel on the server. +2% per
// level, max 20% at Lvl 10.
const DefensivePctPerLevel = 0.02;
// Warehouse safe-storage mirrors CastleService.WarehouseSafePerResource:
// the protected floor that PvP attackers cannot steal — Lvl 1 = 50k per
// resource, then ×1.30 per level (Lvl 5 ≈ 143k, Lvl 10 ≈ 530k, Lvl 30 ≈ 7.5B).
const WarehouseSafeBase = 50_000;
const WarehouseSafeMultiplier = 1.30;
function warehouseSafePerResource(level: number): number {
  if (level <= 0) return 0;
  return Math.floor(WarehouseSafeBase * Math.pow(WarehouseSafeMultiplier, level - 1));
}
const BeginnerShieldMaxCastleLevel = 10;

const WarHallUnlockHints = [
  "Слот 1 доступен сразу",
  "Слот 2 при Замке Lvl 10",
  "Слот 3 при Замке Lvl 20",
  "Слот 4 — донат-апгрейд",
];

export interface BuildingMeta {
  description: string;
  effectAtLevel: (level: number) => string | null;
}

export const BuildingMetaByType: Record<number, BuildingMeta> = {
  // Castle
  0: {
    description:
      "Главное здание. Его уровень ограничивает максимум всех остальных построек.",
    effectAtLevel: (lvl) => `Потолок зданий: Lvl ${lvl}`,
  },
  // GoldMine
  1: {
    description: "Производит золото. Сбор автоматический.",
    effectAtLevel: () => null,
  },
  // Lumberjack
  2: {
    description: "Производит древесину. Сбор автоматический.",
    effectAtLevel: () => null,
  },
  // Quarry
  3: {
    description: "Производит камень. Сбор автоматический.",
    effectAtLevel: () => null,
  },
  // ManaAltar
  4: {
    description: "Производит ману. Сбор автоматический.",
    effectAtLevel: () => null,
  },
  // Warehouse
  5: {
    description:
      "Хранилище защищает часть ресурсов от грабежа при PvP-нападении. " +
      "На неё не действует процент потерь — оппонент уносит только то, что лежит сверх защищённого порога.",
    effectAtLevel: (lvl) =>
      `Защищено от грабежа: ${warehouseSafePerResource(lvl).toLocaleString("ru-RU")} ед. на каждый ресурс`,
  },
  // Barracks
  6: {
    description: "Тренирует пехоту твоей стихии.",
    effectAtLevel: (lvl) => `Тренировка пехоты, Lvl ${lvl}`,
  },
  // ArcheryRange
  7: {
    description: "Тренирует стрелков твоей стихии.",
    effectAtLevel: (lvl) => `Тренировка стрелков, Lvl ${lvl}`,
  },
  // MageTower
  8: {
    description: "Тренирует магов твоей стихии.",
    effectAtLevel: (lvl) => `Тренировка магов, Lvl ${lvl}`,
  },
  // SignatureForge
  9: {
    description: "Тренирует особых юнитов твоей стихии.",
    effectAtLevel: (lvl) => `Тренировка особых, Lvl ${lvl}`,
  },
  // Hospital
  10: {
    description:
      "После боя часть тяжело раненых попадает сюда. Уровень определяет вместимость; переполнение превращает раненых в мёртвых навсегда.",
    effectAtLevel: (lvl) =>
      `Вместимость: ${(HospitalCapacityPerLevel * lvl).toLocaleString("ru-RU")} раненых`,
  },
  // WarHall
  11: {
    description: `Каждый построенный WarHall = один параллельный марш. ${WarHallUnlockHints.join(
      " · ",
    )}.`,
    effectAtLevel: (lvl) => `Слотов марша: ${lvl}`,
  },
  // Academy
  12: {
    description: "Открывает исследования: бонусы к производству, бою, скоростям.",
    effectAtLevel: () =>
      AcademyPctPerLevel > 0 ? `+${Math.round(AcademyPctPerLevel * 100)}% за уровень` : null,
  },
  // HeroHall
  13: {
    description: "Призыв героев. Уровень определяет редкости, доступные для найма.",
    effectAtLevel: (lvl) => {
      if (lvl >= 10) return "Доступны: Common · Rare · Epic · Legendary";
      if (lvl >= 5) return "Доступны: Common · Rare · Epic";
      if (lvl >= 3) return "Доступны: Common · Rare";
      return lvl >= 1 ? "Доступны: Common" : "Не построен";
    },
  },
  // BuilderHut
  14: {
    description: "Сокращает время постройки всех зданий.",
    effectAtLevel: (lvl) =>
      BuilderHutPctPerLevel > 0
        ? `−${Math.round(BuilderHutPctPerLevel * lvl * 100)}% к времени постройки`
        : null,
  },
  // TempleOfResurrection
  15: {
    description:
      "Защитная постройка. 70% «мёртвых» защитника попадают сюда и медленно возрождаются без ресурсов.",
    effectAtLevel: (lvl) =>
      `Очередь возрождения: ${(TempleQueueCapacityPerLevel * lvl).toLocaleString("ru-RU")} мест`,
  },
  // Wall
  16: {
    description:
      "Крепостная стена. Поднимает Защиту всех войск защитника, пока он сидит в своём замке. Работает только в обороне — в наступательных маршах бонус не применяется.",
    effectAtLevel: (lvl) =>
      `+${Math.round(DefensivePctPerLevel * lvl * 100)}% Защиты защитнику в этом замке`,
  },
  // ArrowTower
  17: {
    description:
      "Лучная башня. Поднимает Атаку всех войск защитника, пока он сидит в своём замке. Работает только в обороне — в наступательных маршах бонус не применяется.",
    effectAtLevel: (lvl) =>
      `+${Math.round(DefensivePctPerLevel * lvl * 100)}% Атаки защитнику в этом замке`,
  },
  // TrapField
  18: {
    description:
      "Ловушки. В первом раунде боя у твоего замка снижают входящий урон по защитникам — даёт ощутимое преимущество в первом обмене ударами. Работает только в обороне.",
    effectAtLevel: (lvl) =>
      `−${Math.round(DefensivePctPerLevel * lvl * 100)}% входящего урона в первый раунд`,
  },
};

export { BeginnerShieldMaxCastleLevel };
